/**
 * Obstacle-to-scene coordinate conversion utility
 *
 * Extracts the obstacle→scene-position math (building-local + rotation + offset)
 * into a reusable function. Used by ObstacleLabelsOverlay, BuildingEditorMap focus,
 * and obstacle rotation ring positioning.
 */

import type { Obstacle } from '@/types/obstacle'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

/**
 * Compute the scene-absolute position (in feet) for an obstacle.
 *
 * Steps:
 * 1. Find the building containing the obstacle's unit
 * 2. Apply unit offset with centering adjustment
 * 3. Rotate by building bearing (Three.js Y-axis convention)
 * 4. Add building world position
 *
 * @returns { x, z } in scene-absolute feet, or null if building not found
 */
export function getObstacleScenePosition(
  obstacle: Obstacle,
  buildings: Building3DConfigExtended[]
): { x: number; z: number } | null {
  // Find building containing this obstacle's unit
  let building: Building3DConfigExtended | undefined
  for (const b of buildings) {
    if (b.generatedUnits?.some((u) => u.id === obstacle.unitId)) {
      building = b
      break
    }
  }
  if (!building) return null

  // Find unit offsets
  const unit = building.generatedUnits?.find((u) => u.id === obstacle.unitId)
  const unitOffsetX = unit?.offset_x ?? 0
  const unitOffsetY = unit?.offset_y ?? 0

  // Centering adjustment (same as ObstacleRenderer)
  const centerX = building.width / 2
  const tpl = building.templateType as string | undefined
  const centerZ = (tpl === 'double_row' || tpl === 'nested_t_hangar') ? building.depth / 2 : 0
  const adjOffsetX = unitOffsetX - centerX
  const adjOffsetY = unitOffsetY - centerZ

  // Building-local position in feet
  let localX = adjOffsetX + obstacle.positionX
  let localZ = -(adjOffsetY + obstacle.positionY)

  // Apply building rotation (R_2d(-θ), Three.js Y-axis convention)
  const buildingRotDeg = building.rotation ?? 0
  if (buildingRotDeg !== 0) {
    const rad = -(buildingRotDeg * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const rx = localX * cos - localZ * sin
    const rz = localX * sin + localZ * cos
    localX = rx
    localZ = rz
  }

  // Add building world position to get scene-absolute feet
  return {
    x: building.position[0] + localX,
    z: building.position[2] + localZ,
  }
}
